import React, { useEffect, useRef } from 'react';
import { X, FileText, Download, Copy } from 'lucide-react';

export default function DocumentViewer({ doc, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const trap = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [onClose]);

  function handleCopy() {
    navigator.clipboard.writeText(doc.notes || '');
  }

  function handleDownload() {
    const blob = new Blob([doc.notes || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.name}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="viewer-title" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-[#0D9488] shrink-0" aria-hidden="true" />
            <h2 id="viewer-title" className="text-lg font-bold text-[#0F1C2E] truncate">{doc.name}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleCopy} aria-label="Copy content" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
              <Copy className="w-5 h-5" aria-hidden="true" />
            </button>
            <button onClick={handleDownload} aria-label="Download as text" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
              <Download className="w-5 h-5" aria-hidden="true" />
            </button>
            <button ref={closeRef} onClick={onClose} aria-label="Close viewer" className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0D9488]">
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3 text-sm text-slate-500 flex-wrap">
          <span>Created by: <strong className="text-slate-700">{doc.uploaded_by_name || 'Unknown'}</strong></span>
          <span aria-hidden="true">•</span>
          <span>{doc.created_date ? new Date(doc.created_date).toLocaleDateString('en-GB') : '—'}</span>
          {doc.tags?.length > 0 && (
            <>
              <span aria-hidden="true">•</span>
              <div className="flex gap-1 flex-wrap">
                {doc.tags.map(tag => <span key={tag} className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full">{tag}</span>)}
              </div>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{doc.notes || 'No content available.'}</pre>
        </div>
      </div>
    </div>
  );
}